import React, { useEffect, useState, useRef } from 'react';
import { ArrowDownIcon } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
export function HeroSection() {
  const {
    t,
    direction
  } = useLanguage();
  const [activeSlide, setActiveSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slides = [{
    heading: t('hero.slide1.heading'),
    description: t('hero.slide1.description')
  }, {
    heading: t('hero.slide2.heading'),
    description: t('hero.slide2.description')
  }];
  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true);
      setActiveSlide(prev => prev + 1);
    }, 7000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (activeSlide === slides.length) {
      timeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
        setActiveSlide(0);
      }, 700);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [activeSlide, slides.length]);
  const handleDotClick = (index: number) => {
    setIsTransitioning(true);
    setActiveSlide(index);
  };
  const handleScrollDown = () => {
    const hero = document.getElementById('hero-section');
    if (hero) {
      const nextSection = hero.nextElementSibling as HTMLElement;
      if (nextSection) {
        nextSection.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }
  };
  const displaySlides = [...slides, slides[0]];
  // Handle RTL transform logic
  const transformValue = direction === 'rtl' ? `translateX(${activeSlide * 100}%)` : `translateX(-${activeSlide * 100}%)`;
  return <section id="hero-section" className="relative min-h-screen overflow-hidden bg-[#031458]" dir={direction} data-id="element-178">
      {/* Background Video */}
      <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" data-id="element-179">
        <source src="https://cdn.magicpatterns.com/uploads/g3s7fjBaL9nccK2cNGrzgP/hero-vid.mp4" type="video/mp4" data-id="element-180" />
      </video>
      <div className="absolute inset-0 bg-[#031458]/30" data-id="element-181" />

      {/* Slides */}
      <div className="relative z-10 w-full min-h-screen overflow-hidden" data-id="element-182">
        <div className="flex min-h-screen" style={{
        transform: transformValue,
        transition: isTransitioning ? 'transform 700ms ease-in-out' : 'none'
      }} data-id="element-183">
          {displaySlides.map((slide, index) => <div key={index} className="w-full flex-shrink-0 min-h-screen flex items-center" data-id="element-184">
              <div className="container mx-auto px-6 md:px-12" data-id="element-185">
                <div className="text-white max-w-3xl" data-id="element-186">
                  <h1 className="font-semibold leading-[1.05] mb-10 uppercase text-[32px] md:text-[44px] lg:text-[55px] whitespace-pre-line" data-id="element-187">
                    {slide.heading}
                  </h1>
                  <p className="text-base md:text-lg text-white mb-10 max-w-lg leading-relaxed" data-id="element-188">
                    {slide.description}
                  </p>
                  <button className="btn-primary w-[200px] h-[50px]" data-id="element-189">
                    {t('hero.learnMore')}
                  </button>
                </div>
              </div>
            </div>)}
        </div>
      </div>

      {/* Pagination Dots */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2" data-id="element-190">
        {slides.map((_, index) => <button key={index} onClick={() => handleDotClick(index)} className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${activeSlide % slides.length === index ? 'bg-white' : 'bg-white/40 hover:bg-white/60'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-191" />)}
      </div>

      {/* Scroll Down Indicator */}
      <button onClick={handleScrollDown} className={`absolute bottom-10 ${direction === 'rtl' ? 'left-10' : 'right-10'} z-10 hidden md:flex flex-col items-center gap-3 cursor-pointer bg-transparent border-none`} data-id="element-192">
        <span className="text-white/70 text-xs font-medium tracking-widest uppercase" style={{
        writingMode: 'vertical-rl'
      }} data-id="element-193">
          {t('hero.scrollDown')}
        </span>
        <ArrowDownIcon className="w-4 h-4 text-white/70 animate-bounce" data-id="element-194" />
      </button>
    </section>;
}